import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {DataTable} from 'primereact/datatable';
import {Column} from 'primereact/column';
import {Button} from 'primereact/button';
import {InputText} from 'primereact/inputtext';
import {Dropdown} from 'primereact/dropdown';
import {MultiSelect} from 'primereact/multiselect';
import {Calendar} from 'primereact/calendar';
import {Tag} from 'primereact/tag';
import {Toast} from 'primereact/toast';
import {Card} from 'primereact/card';
import {ConfirmDialog} from 'primereact/confirmdialog';
import {Badge} from 'primereact/badge';
import {IconField} from 'primereact/iconfield';
import {InputIcon} from 'primereact/inputicon';
import {Dialog} from 'primereact/dialog';

import {deleteQuote, getQuotesList, updateQuoteStatus} from './integracion';
import {exportarCotizacionesExcel} from './exportarCotizacionesExcel';
import FlujoCotizaciones from '../FlujoCotizaciones/FlujoCotizaciones';
import './ListadoCotizaciones.scss';
import {desEncriptar} from "../../../utilidades/encriptacion";
import {InputTextarea} from "primereact/inputtextarea";


// ─── Configuración de estatus ───
const ESTATUS_CONFIG = {
    'NUEVO':                            { severity: 'info',      icon: 'pi pi-file'         },
    'EN PLANEACIÓN':                    { severity: 'primary',   icon: 'pi pi-pencil'       },
    'EN COSTEO':                        { severity: 'secondary', icon: 'pi pi-calculator'   },
    'ESPERANDO APROBACIÓN DEL CLIENTE': { severity: 'danger',    icon: 'pi pi-clock'        },
    'APROBADO':                         { severity: 'success',   icon: 'pi pi-check-circle' },
    'EN PRODUCCIÓN':                    { severity: 'success',   icon: 'pi pi-cog'          },
    'RECHAZADO':                        { severity: 'danger',    icon: 'pi pi-times'        },
    'SOLICITUD DE CAMBIOS':             { severity: 'danger',    icon: 'pi pi-sync'         },
    'COSTEADO':                         { severity: 'warning',   icon: 'pi pi-receipt'      },
};

const OPCIONES_ESTATUS = [
    { label: 'Nuevo',                value: 1 },
    { label: 'En planeación',        value: 2 },
    { label: 'En costeo',            value: 3 },
    { label: 'Esperando aprobación', value: 4 },
    { label: 'Aprobado',             value: 5 },
    { label: 'En producción',        value: 6 },
    { label: 'Rechazado',            value: 7 },
    { label: 'Solicitud de cambios', value: 8 },
    { label: 'Costeado',             value: 9 },
];

// Opciones para el dialog de cambio (sin "Todos")
const OPCIONES_CAMBIO_ESTATUS = [
    { label: 'Nuevo',                value: 1 },
    { label: 'En planeación',        value: 2 },
    { label: 'En costeo',            value: 3 },
    { label: 'Esperando aprobación', value: 4 },
    { label: 'Solicitud de cambios', value: 8 },
    { label: 'Costeado',             value: 9 },
];

// El filtro de fecha solo aplica a cotizaciones En producción o Rechazadas;
// el resto de estatus son "en curso"/pendientes y no se acotan por fecha.
const ID_ESTATUS_PRODUCCION = 6;
const ID_ESTATUS_RECHAZADO = 7;

// Estatus que se envían al backend cuando el usuario no eligió ninguno
// explícitamente: todos menos En producción y Rechazado.
const ESTATUS_ID_POR_DEFECTO = OPCIONES_ESTATUS
    .map((o) => o.value)
    .filter((v) => v !== ID_ESTATUS_PRODUCCION && v !== ID_ESTATUS_RECHAZADO);

const TARJETAS_ESTATUS = [
    { value: 1, label: 'Nuevo',                icon: 'pi pi-file',         color: '#3B82F6' },
    { value: 2, label: 'En planeación',        icon: 'pi pi-pencil',       color: '#6366F1' },
    { value: 3, label: 'En costeo',            icon: 'pi pi-calculator',   color: '#8B5CF6' },
    { value: 4, label: 'Esperando aprobación', icon: 'pi pi-clock',        color: '#F59E0B' },
    { value: 5, label: 'Aprobado',              icon: 'pi pi-check-circle', color: '#22C55E' },
    { value: 6, label: 'En producción',        icon: 'pi pi-cog',          color: '#16A34A' },
    { value: 7, label: 'Rechazado',            icon: 'pi pi-times',        color: '#EF4444' },
    { value: 8, label: 'Solicitud de cambios', icon: 'pi pi-sync',         color: '#DC2626' },
    { value: 9, label: 'Costeado',             icon: 'pi pi-receipt',      color: '#EAB308' },
];

// ─── Helper: obtener ID del usuario logueado ───
const obtenerUsuarioId = () => {
    try {
        return JSON.parse(
            desEncriptar(
                sessionStorage.getItem('dataClient'),
                process.env.REACT_APP_CLAVE_INCRIPTACION
            )
        )
    } catch {
        return null;
    }
};

// ─── Persistencia de filtros/orden en sessionStorage ───
const SESSION_KEY = 'listadoCotizaciones_filtros';

const cargarEstadoGuardado = () => {
    try {
        const guardado = sessionStorage.getItem(SESSION_KEY);
        return guardado ? JSON.parse(guardado) : {};
    } catch {
        return {};
    }
};

// Convierte un Date a "YYYY-MM-DD" usando componentes locales (evita el
// desfase de un día que produce toISOString con zonas horarias negativas).
const aFechaISO = (fecha) => {
    if (!fecha) return undefined;
    const y = fecha.getFullYear();
    const m = String(fecha.getMonth() + 1).padStart(2, '0');
    const d = String(fecha.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

// Rango por defecto del filtro de fecha: últimos 30 días hasta hoy.
const obtenerRangoUltimos30Dias = () => {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);

    const hace30Dias = new Date(hoy);
    hace30Dias.setDate(hoy.getDate() - 29);

    return { inicio: hace30Dias, fin: hoy };
};


const ListadoCotizaciones = ({
                                 onNuevaCotizacion,
                                 onNuevaCotizacionEdicion,
                                 onVerDetalle,
                                 onEditarCotizacion,
                                 onEditarCotizacionMod
                             }) => {
    const toastRef = useRef(null);
    const dt = useRef(null);

    const [cotizaciones, setCotizaciones] = useState([]);
    const [cargando, setCargando] = useState(true);

    // Lazy initializers: sessionStorage solo se lee UNA VEZ al montar el componente,
    // no en cada render.
    const [busqueda, setBusqueda] = useState(() => cargarEstadoGuardado().busqueda ?? '');
    const [filtroEstatus, setFiltroEstatus] = useState(() => {
        const valor = cargarEstadoGuardado().filtroEstatus;
        // Normaliza a arreglo de números; compatible con el valor anterior
        // (un solo número) guardado en sesiones previas al multiselect.
        if (Array.isArray(valor)) return valor.filter((v) => typeof v === 'number');
        if (typeof valor === 'number') return [valor];
        return [];
    });
    const [sortField, setSortField] = useState(() => cargarEstadoGuardado().sortField ?? 'fechaCreacion');
    const [sortOrder, setSortOrder] = useState(() => {
        const valor = cargarEstadoGuardado().sortOrder;
        return valor === 1 || valor === -1 ? valor : -1;
    });
    const [fechaInicio, setFechaInicio] = useState(() => {
        const valor = cargarEstadoGuardado().fechaInicio;
        if (valor) return new Date(valor);
        if (valor === null) return null; // el usuario limpió el filtro de fecha explícitamente
        return obtenerRangoUltimos30Dias().inicio; // primera visita: últimos 30 días
    });
    const [fechaFin, setFechaFin] = useState(() => {
        const valor = cargarEstadoGuardado().fechaFin;
        if (valor) return new Date(valor);
        if (valor === null) return null;
        return obtenerRangoUltimos30Dias().fin;
    });

    // ─── Estado del dialog de cambio de estatus ───
    const [dialogVisible, setDialogVisible] = useState(false);
    const [cotizacionSeleccionada, setCotizacionSeleccionada] = useState(null);
    const [nuevoEstatusId, setNuevoEstatusId] = useState(null);
    const [guardandoEstatus, setGuardandoEstatus] = useState(false);
    const [solicitudCambios, setSolicitudCambios] = useState('');

    const [flujoVisible, setFlujoVisible] = useState(false);

    const [eliminarDialogVisible, setEliminarDialogVisible] = useState(false);
    const [cotizacionAEliminar, setCotizacionAEliminar] = useState(null);
    const [motivoEliminacion, setMotivoEliminacion] = useState('');
    const [eliminando, setEliminando] = useState(false);

    const ESTATUS_ELIMINABLES = [1, 2, 3, 8];

    const puedeEliminarCotizacion = useCallback((cotizacion) => {
        return ESTATUS_ELIMINABLES.includes(cotizacion.estatusId);
    }, []);

    const userData = obtenerUsuarioId();

    const permisos = userData?.permissions ?? [];
    const getPermisosModulo = (permisos, padre, modulo) => {
        const grupo = permisos.find(g => g.padre === padre);
        if (!grupo) return [];
        const mod = grupo.modulos.find(m => m.nombre === modulo);
        return mod?.permisos ?? [];
    };

    // Guarda filtros y orden cada vez que cambien
    useEffect(() => {
        sessionStorage.setItem(
            SESSION_KEY,
            JSON.stringify({
                busqueda,
                filtroEstatus,
                sortField,
                sortOrder,
                fechaInicio: fechaInicio ? aFechaISO(fechaInicio) : null,
                fechaFin: fechaFin ? aFechaISO(fechaFin) : null,
            })
        );
    }, [busqueda, filtroEstatus, sortField, sortOrder, fechaInicio, fechaFin]);

    const permisosModulo = getPermisosModulo(
        permisos,
        "Cotizaciones empresariales",
        "Listado de cotizaciones"
    );

    const tienePermiso = (nombre) => permisosModulo.includes(nombre);


    // ─── Permisos ───
    const puedeEditarEstatus = useMemo(() => {
        return tienePermiso("CAMBIAR ESTATUS");
    }, []);

    const puedeEliminar = useMemo(() => {
        return tienePermiso("BORRAR COTIZACIÓN");
    }, []);

    // El filtro de fecha solo se muestra/aplica cuando el usuario está viendo
    // cotizaciones En producción o Rechazadas.
    const mostrarFiltroFecha =
        filtroEstatus.includes(ID_ESTATUS_PRODUCCION) || filtroEstatus.includes(ID_ESTATUS_RECHAZADO);

    // Estatus a enviar al backend: la selección del usuario, o el default
    // (todos menos En producción/Rechazado) si no eligió ninguno.
    const estatusParaBackend = filtroEstatus.length > 0 ? filtroEstatus : ESTATUS_ID_POR_DEFECTO;

    // ── Carga ──

    const cargarCotizaciones = async () => {
        try {
            setCargando(true);
            // El backend filtra por estatus y, cuando corresponde, por rango
            // de fechas (solo aplica si se está viendo Aprobado/Rechazado).
            const response = await getQuotesList({
                estatus: estatusParaBackend,
                ...(mostrarFiltroFecha
                    ? {start: aFechaISO(fechaInicio), end: aFechaISO(fechaFin)}
                    : {}),
            });
            if (response.success) {
                setCotizaciones(response.data);
            } else {
                toastRef.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'No se pudieron cargar las cotizaciones',
                    life: 4000,
                });
            }
        } catch (error) {
            console.error('Error:', error);
        } finally {
            setCargando(false);
        }
    };

    useEffect(() => {
        cargarCotizaciones();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtroEstatus, fechaInicio, fechaFin]);

    // ── Filtrado local ──
    // El estatus y la fecha ya vienen filtrados desde el backend; aquí solo
    // se aplica la búsqueda de texto libre.

    const datosFiltrados = useMemo(() => {
        let resultado = cotizaciones;

        if (busqueda.trim()) {
            const q = busqueda.toLowerCase().trim();
            resultado = resultado.filter(
                (c) =>
                    c.nombre?.toLowerCase().includes(q) ||
                    c.clienteNombre?.toLowerCase().includes(q) ||
                    c.ticketNombre?.toLowerCase().includes(q) ||
                    c.usuarioNombre?.toLowerCase().includes(q)
            );
        }

        return resultado;
    }, [cotizaciones, busqueda]);

    const conteoPorEstatus = useMemo(() => {
        return cotizaciones.reduce((acc, c) => {
            const id = Number(c.estatusId);
            acc[id] = (acc[id] ?? 0) + 1;
            return acc;
        }, {});
    }, [cotizaciones]);

    // El rango de fecha por defecto (últimos 30 días) no cuenta como "filtro
    // activo"; solo cuenta si el usuario lo cambió o lo limpió.
    const rangoUltimos30Dias = obtenerRangoUltimos30Dias();
    const fechaEsPorDefecto =
        aFechaISO(fechaInicio) === aFechaISO(rangoUltimos30Dias.inicio) &&
        aFechaISO(fechaFin) === aFechaISO(rangoUltimos30Dias.fin);

    const hayFiltros = busqueda || filtroEstatus.length > 0 || (mostrarFiltroFecha && !fechaEsPorDefecto);

    const limpiarFiltros = () => {
        setBusqueda('');
        setFiltroEstatus([]);
        setFechaInicio(rangoUltimos30Dias.inicio);
        setFechaFin(rangoUltimos30Dias.fin);
        setSortField('fechaCreacion');
        setSortOrder(-1);
    };

    // ── Cambio de estatus ──

    const abrirDialogEstatus = useCallback((cotizacion) => {
        setCotizacionSeleccionada(cotizacion);
        setNuevoEstatusId(cotizacion.estatusId); // preselecciona el actual
        setDialogVisible(true);
    }, []);

    const cerrarDialogEstatus = () => {
        setDialogVisible(false);
        setCotizacionSeleccionada(null);
        setNuevoEstatusId(null);
        setSolicitudCambios('');
    };


    const abrirDialogEliminar = useCallback((cotizacion) => {
        setCotizacionAEliminar(cotizacion);
        setMotivoEliminacion('');
        setEliminarDialogVisible(true);
    }, []);

    const cerrarDialogEliminar = () => {
        setEliminarDialogVisible(false);
        setCotizacionAEliminar(null);
        setMotivoEliminacion('');
    };


// opciones permitidas (solo menores al actual)
    const opcionesPermitidas = useMemo(() => {
        const estatusActual = cotizacionSeleccionada?.estatusId;

        if (!estatusActual) return [];
        if (estatusActual === 6) return []; // En producción: no se puede cambiar

        return OPCIONES_CAMBIO_ESTATUS.filter((op) => op.value < estatusActual);
    }, [cotizacionSeleccionada]);

    const confirmarCambioEstatus = async () => {
        if (!cotizacionSeleccionada || !nuevoEstatusId) return;

        if (nuevoEstatusId === cotizacionSeleccionada.estatusId) {
            toastRef.current?.show({
                severity: 'warn',
                summary: 'Sin cambios',
                detail: 'El estatus seleccionado es el mismo que el actual',
                life: 3000,
            });
            return;
        }

        try {
            setGuardandoEstatus(true);

            const res = await updateQuoteStatus(cotizacionSeleccionada.id, nuevoEstatusId, solicitudCambios);

            if (res.success) {
                // Actualizar localmente
                setCotizaciones((prev) =>
                    prev.map((c) =>
                        c.id === cotizacionSeleccionada.id
                            ? {...c, estatusId: nuevoEstatusId, estatus: res.data?.estatus ?? c.estatus}
                            : c
                    )
                );

                toastRef.current?.show({
                    severity: 'success',
                    summary: 'Estatus actualizado',
                    detail: `"${cotizacionSeleccionada.nombre}" actualizada correctamente`,
                    life: 3000,
                });

                cerrarDialogEstatus();
            } else {
                toastRef.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: res.message || 'No se pudo actualizar el estatus',
                    life: 4000,
                });
            }
        } catch (error) {
            console.error('Error al cambiar estatus:', error);
            toastRef.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Ocurrió un error inesperado',
                life: 4000,
            });
        } finally {
            setGuardandoEstatus(false);
        }
    };

    // ── Eliminar ──

    const confirmarEliminacion = async () => {
        if (!cotizacionAEliminar || !motivoEliminacion.trim()) return;

        try {
            setEliminando(true);

            const res = await deleteQuote(cotizacionAEliminar.id, motivoEliminacion.trim(), userData?.user?.ID);

            if (res.success) {
                setCotizaciones((prev) => prev.filter((c) => c.id !== cotizacionAEliminar.id));

                toastRef.current?.show({
                    severity: 'success',
                    summary: 'Eliminada',
                    detail: `"${cotizacionAEliminar.nombre}" eliminada`,
                    life: 3000,
                });

                cerrarDialogEliminar();
            } else {
                toastRef.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: res.message || 'No se pudo eliminar',
                    life: 4000,
                });
            }
        } catch (error) {
            console.error('Error al eliminar:', error);
            toastRef.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Ocurrió un error inesperado',
                life: 4000,
            });
        } finally {
            setEliminando(false);
        }
    };


    // ── Exportar Excel ──

    const handleExportarExcel = () => {
        exportarCotizacionesExcel(datosFiltrados);
        toastRef.current?.show({
            severity: 'success',
            summary: 'Exportado',
            detail: 'Excel descargado',
            life: 3000,
        });
    };

    // ── Templates de columnas ──

    const nombreBodyTemplate = (rowData) => (
        <span className="font-semibold">{rowData.nombre}</span>
    );

    const clienteBodyTemplate = (rowData) => (
        <div className="flex align-items-center gap-2">
            <i className="pi pi-building text-500" style={{fontSize: '0.9rem'}}></i>
            <span>{rowData.clienteNombre}</span>
        </div>
    );

    const ticketBodyTemplate = (rowData) => (
        <span className="ticket-badge">
            <i className="pi pi-ticket mr-1"></i>
            {rowData.ticketNombre}
        </span>
    );

    const estatusBodyTemplate = (rowData) => {
        const config = ESTATUS_CONFIG[rowData.estatus] || {
            severity: null,
            icon: 'pi pi-question-circle',
        };
        return (
            <Tag
                value={rowData.estatus}
                severity={config.severity}
                icon={config.icon}
                className="estatus-tag"
            />
        );
    };

    const unidadBodyTemplate = (rowData) => (
        <div className="flex align-items-center gap-2">
            <i className="pi pi-car text-500"></i>
            <span>{rowData.tipoUnidadNombre || '—'}</span>
        </div>
    );

    const fechaTentativaBodyTemplate = (rowData) => (
        rowData.fechaTentativa
            ? (
                <div className="flex align-items-center gap-2">
                    <i className="pi pi-calendar-clock text-500" style={{fontSize: '0.9rem'}}></i>
                    <span>{rowData.fechaTentativa}</span>
                </div>
            )
            : <span className="text-500">—</span>
    );

    const turnosBodyTemplate = (rowData) => (
        <Badge
            value={rowData.totalTurnos ?? 0}
            severity={rowData.totalTurnos > 0 ? 'success' : 'warning'}
        />
    );

    const usuarioBodyTemplate = (rowData) => (
        <div className="flex align-items-center gap-2">
            <i className="pi pi-user text-500" style={{fontSize: '0.9rem'}}></i>
            <span>{rowData.usuarioNombre || '—'}</span>
        </div>
    );

    const accionesBodyTemplate = (rowData) => (
        <div className="flex gap-1 justify-content-center">
            <Button
                icon="pi pi-eye"
                className="p-button-rounded p-button-text p-button-sm"
                tooltip="Ver detalle"
                tooltipOptions={{position: 'top'}}
                onClick={() => onVerDetalle?.(rowData.id)}
            />
            {(rowData.estatusId === 1 || rowData.estatusId === 8) && rowData.idCategoria === 3 && (
                <Button
                    icon="pi pi-pencil"
                    className="p-button-rounded p-button-text p-button-info p-button-sm"
                    tooltip="Editar"
                    tooltipOptions={{position: 'top'}}
                    onClick={() => onEditarCotizacion?.(rowData.id)}
                />
            )}
            {(rowData.estatusId === 1 || rowData.estatusId === 2 || rowData.estatusId === 8) && rowData.idCategoria === 5 && (
                <Button
                    icon="pi pi-pencil"
                    className="p-button-rounded p-button-text p-button-info p-button-sm"
                    tooltip="Editar"
                    tooltipOptions={{position: 'top'}}
                    onClick={() => onEditarCotizacionMod?.(rowData.id)}
                />
            )}
            {puedeEditarEstatus && (
                <Button
                    icon="pi pi-sync"
                    className="p-button-rounded p-button-text p-button-warning p-button-sm"
                    tooltip="Cambiar estatus"
                    tooltipOptions={{position: 'top'}}
                    onClick={() => abrirDialogEstatus(rowData)}
                />
            )}
            {puedeEliminar && puedeEliminarCotizacion(rowData) && (
                <Button
                    icon="pi pi-trash"
                    className="p-button-rounded p-button-text p-button-danger p-button-sm"
                    tooltip="Eliminar"
                    tooltipOptions={{position: 'top'}}
                    onClick={() => abrirDialogEliminar(rowData)}
                />
            )}

        </div>
    );

    // ── Header ──

    const header = (
        <div className="tabla-header p-3">
            {/* ====== BLOQUE SUPERIOR ====== */}
            <div className="flex flex-column md:flex-row md:align-items-center md:justify-content-between gap-3">
                {/* Título + contador */}
                <div className="flex align-items-center gap-2">
                    <i
                        className="pi pi-file-edit"
                        style={{fontSize: '1.4rem', color: 'var(--primary-color)'}}
                    />
                    <h2 className="m-0 text-xl">Cotizaciones</h2>
                    <Badge value={datosFiltrados.length} severity="info"/>
                </div>

                {/* Acciones */}
                <div className="flex flex-wrap gap-2 justify-content-start md:justify-content-end">
                    <Button
                        icon="pi pi-sitemap"
                        label="Revisar flujo"
                        className="p-button-outlined p-button-sm"
                        onClick={() => setFlujoVisible(true)}
                    />
                    <Button
                        icon="pi pi-refresh"
                        className="p-button-outlined p-button-sm"
                        tooltip="Actualizar"
                        onClick={cargarCotizaciones}
                        loading={cargando}
                    />
                    <Button
                        icon="pi pi-file-excel"
                        label="Excel"
                        className="p-button-outlined p-button-sm"
                        onClick={handleExportarExcel}
                        disabled={datosFiltrados.length === 0}
                    />
                    <Button
                        icon="pi pi-plus"
                        label="Cotizar Nueva Ruta"
                        className="p-button-sm"
                        onClick={onNuevaCotizacion}
                    />
                    <Button
                        icon="pi pi-plus"
                        label="Cotizar Edición de Ruta"
                        className="p-button-sm"
                        onClick={onNuevaCotizacionEdicion}
                    />
                </div>
            </div>

            {/* ====== CHIPS DE ESTATUS ====== */}
            <div className="estatus-chips">
                {TARJETAS_ESTATUS.map((t) => (
                    <button
                        key={t.value}
                        type="button"
                        className={`estatus-chip${filtroEstatus.includes(t.value) ? ' active' : ''}`}
                        style={{ '--chip-color': t.color }}
                        onClick={() =>
                            setFiltroEstatus((prev) =>
                                prev.includes(t.value)
                                    ? prev.filter((v) => v !== t.value)
                                    : [...prev, t.value]
                            )
                        }
                    >
                        <i className={t.icon} />
                        <span className="estatus-chip-numero">{conteoPorEstatus[t.value] ?? 0}</span>
                        <span className="estatus-chip-label">{t.label}</span>
                    </button>
                ))}
            </div>

            {/* ====== FILTROS ====== */}
            <div className="grid mt-3">
                {/* Buscador */}
                <div className="col-12 md:col-4">
                    <IconField iconPosition="left" className="w-full">
                        <InputIcon className="pi pi-search"/>
                        <InputText
                            value={busqueda}
                            onChange={(e) => setBusqueda(e.target.value)}
                            placeholder="Buscar por nombre, cliente, ticket o usuario..."
                            className="w-full"
                        />
                    </IconField>
                </div>

                {/* Estatus */}
                <div className={`col-12 ${mostrarFiltroFecha ? 'md:col-2' : 'md:col-3'}`}>
                    <MultiSelect
                        value={filtroEstatus}
                        options={OPCIONES_ESTATUS}
                        onChange={(e) => setFiltroEstatus(e.value)}
                        placeholder="Estatus"
                        display="chip"
                        className="w-full"
                    />
                </div>

                {/* Fecha: solo aplica al ver cotizaciones Aprobadas/Rechazadas */}
                {mostrarFiltroFecha && (
                    <>
                        {/* Fecha desde */}
                        <div className="col-12 md:col-2">
                            <Calendar
                                value={fechaInicio}
                                onChange={(e) => setFechaInicio(e.value ?? null)}
                                placeholder="Desde"
                                dateFormat="dd/mm/yy"
                                maxDate={fechaFin ?? undefined}
                                showIcon
                                showButtonBar
                                className="w-full"
                            />
                        </div>

                        {/* Fecha hasta */}
                        <div className="col-12 md:col-2">
                            <Calendar
                                value={fechaFin}
                                onChange={(e) => setFechaFin(e.value ?? null)}
                                placeholder="Hasta"
                                dateFormat="dd/mm/yy"
                                minDate={fechaInicio ?? undefined}
                                showIcon
                                showButtonBar
                                className="w-full"
                            />
                        </div>
                    </>
                )}

                {/* Limpiar */}
                {hayFiltros && (
                    <div className="col-12 md:col-2 flex align-items-center">
                        <Button
                            icon="pi pi-filter-slash"
                            label="Limpiar"
                            className="p-button-outlined p-button-secondary p-button-sm w-full"
                            onClick={limpiarFiltros}
                        />
                    </div>
                )}
            </div>
        </div>
    );

    // ── Estado vacío ──

    const emptyMessage = (
        <div className="flex flex-column align-items-center justify-content-center p-5 text-center">
            <i
                className="pi pi-inbox"
                style={{fontSize: '3rem', color: 'var(--text-color-secondary)'}}
            ></i>
            <h3 className="mt-3 mb-1">No se encontraron cotizaciones</h3>
            <p className="text-600 mb-3">
                {hayFiltros
                    ? 'Intente ajustar los filtros'
                    : 'Comience creando su primera cotización'}
            </p>
            <Button
                icon={hayFiltros ? 'pi pi-filter-slash' : 'pi pi-plus'}
                label={hayFiltros ? 'Limpiar filtros' : 'Crear cotización'}
                className={hayFiltros ? 'p-button-outlined' : ''}
                onClick={hayFiltros ? limpiarFiltros : onNuevaCotizacion}
            />
        </div>
    );

    // ── Footer del Dialog de estatus ──

    const dialogEstatusFooter = (
        <div className="flex justify-content-end gap-2">
            <Button
                label="Cancelar"
                icon="pi pi-times"
                className="p-button-text p-button-secondary"
                onClick={cerrarDialogEstatus}
                disabled={guardandoEstatus}
            />
            <Button
                label="Confirmar cambio"
                icon="pi pi-check"
                className="p-button-sm"
                onClick={confirmarCambioEstatus}
                loading={guardandoEstatus}
                disabled={!nuevoEstatusId || nuevoEstatusId === cotizacionSeleccionada?.estatusId}
            />
        </div>
    );

    return (
        <div className="listado-cotizaciones">
            <Toast ref={toastRef} position="top-right"/>
            <ConfirmDialog/>

            <Card className="tabla-card">
                <DataTable
                    ref={dt}
                    value={datosFiltrados}
                    loading={cargando}
                    header={header}
                    emptyMessage={emptyMessage}
                    sortField={sortField}
                    sortOrder={sortOrder}
                    onSort={(e) => { setSortField(e.sortField); setSortOrder(e.sortOrder); }}
                    removableSort
                    showGridlines
                    scrollable
                    scrollHeight="calc(100vh - 290px)"
                    responsiveLayout="scroll"
                    stripedRows
                    exportFilename={`cotizaciones_${new Date().toISOString().slice(0, 10)}`}
                    className="cotizaciones-datatable"
                    dataKey="id"
                >
                    <Column field="id" header="ID" sortable/>
                    <Column field="nombre" header="Nombre" body={nombreBodyTemplate} sortable
                            style={{minWidth: '180px'}}/>
                    <Column field="estatus" header="Estatus" body={estatusBodyTemplate} sortable
                            style={{minWidth: '170px'}}/>
                    <Column field="clienteNombre" header="Cliente" body={clienteBodyTemplate} sortable
                            style={{minWidth: '160px'}}/>
                    <Column field="ticketNombre" header="Ticket" body={ticketBodyTemplate} sortable
                            style={{minWidth: '130px'}}/>
                    <Column field="fechaCreacion" header="Fecha de creación" sortable style={{minWidth: '140px'}}/>
                    <Column field="fechaTentativa" header="Fecha tentativa" body={fechaTentativaBodyTemplate} sortable
                            style={{minWidth: '130px'}}/>
                    <Column field="categoria" header="Categoria" sortable style={{minWidth: '180px'}}/>
                    <Column field="tipoUnidadNombre" header="Unidad" body={unidadBodyTemplate} sortable
                            style={{minWidth: '130px'}}/>
                    <Column field="totalTurnos" header="Turnos" body={turnosBodyTemplate} sortable
                            style={{minWidth: '80px', textAlign: 'center'}}/>
                    <Column field="usuarioNombre" header="Creado por" body={usuarioBodyTemplate} sortable
                            style={{minWidth: '140px'}}/>
                    <Column header="Acciones" body={accionesBodyTemplate} frozen alignFrozen="right"
                            style={{maxWidth: '160px', textAlign: 'center'}}/>
                </DataTable>
            </Card>

            {/* ════════ Dialog cambio de estatus ════════ */}
            <Dialog
                visible={dialogVisible}
                onHide={cerrarDialogEstatus}
                header="Cambiar estatus de cotización"
                footer={dialogEstatusFooter}
                style={{width: '450px'}}
                modal
                className="dialog-cambio-estatus"
                closable={!guardandoEstatus}
                draggable={false}
            >
                {cotizacionSeleccionada && (
                    <div className="flex flex-column gap-3">
                        {/* Info de la cotización */}
                        <div className="surface-100 border-round p-3">
                            <div className="flex align-items-center gap-2 mb-2">
                                <i className="pi pi-file-edit text-primary"/>
                                <span className="font-semibold text-lg">
                                    {cotizacionSeleccionada.nombre}
                                </span>
                            </div>
                            <div className="flex align-items-center gap-2 text-600">
                                <i className="pi pi-building" style={{fontSize: '0.85rem'}}/>
                                <span className="text-sm">{cotizacionSeleccionada.clienteNombre}</span>
                            </div>
                        </div>

                        {/* Estatus actual */}
                        <div className="flex align-items-center gap-2">
                            <span className="text-600">Estatus actual:</span>
                            {estatusBodyTemplate(cotizacionSeleccionada)}
                        </div>

                        {/* Selector de nuevo estatus */}
                        <div className="flex flex-column gap-2">
                            <label htmlFor="nuevo-estatus" className="font-medium">
                                Nuevo estatus
                            </label>

                            <Dropdown
                                id="nuevo-estatus"
                                value={nuevoEstatusId}
                                options={opcionesPermitidas}
                                onChange={(e) => setNuevoEstatusId(e.value)}
                                placeholder="Seleccione un estatus"
                                className="w-full"
                            />
                        </div>

                        {/* Indicador visual del cambio */}
                        {nuevoEstatusId && nuevoEstatusId !== cotizacionSeleccionada.estatusId && (
                            <div className="flex align-items-center gap-2 p-2 border-round surface-50">
                                <Tag
                                    value={cotizacionSeleccionada.estatus}
                                    severity={
                                        ESTATUS_CONFIG[cotizacionSeleccionada.estatus]?.severity
                                    }
                                    className="estatus-tag"
                                />
                                <i className="pi pi-arrow-right text-500"/>
                                <Tag
                                    value={
                                        OPCIONES_CAMBIO_ESTATUS.find((o) => o.value === nuevoEstatusId)?.label
                                    }
                                    severity="info"
                                    className="estatus-tag"
                                />
                            </div>
                        )}
                        {/* Solicitud de cambios */}
                        <div className="flex flex-column gap-2">
                            <label htmlFor="solicitud-cambios" className="font-medium">
                                Solicitud de cambios
                                <span className="text-400 font-normal ml-1"
                                      style={{fontSize: '0.8rem'}}>(opcional)</span>
                            </label>
                            <InputTextarea
                                id="solicitud-cambios"
                                value={solicitudCambios}
                                onChange={(e) => setSolicitudCambios(e.target.value)}
                                rows={4}
                                autoResize
                                placeholder="Describe los cambios requeridos o el motivo del cambio de estatus..."
                                className="w-full"
                                style={{fontSize: '0.85rem'}}
                            />
                            <small className="text-400">{solicitudCambios?.length ?? 0} caracteres</small>
                        </div>
                    </div>
                )}
            </Dialog>
            {/* ════════ Dialog flujo de cotizaciones ════════ */}
            <Dialog
                visible={flujoVisible}
                onHide={() => setFlujoVisible(false)}
                header="Flujo de cotizaciones empresariales"
                style={{width: '960px'}}
                breakpoints={{'1000px': '95vw'}}
                contentStyle={{padding: 0}}
                maximizable
                modal
                draggable={false}
            >
                <FlujoCotizaciones/>
            </Dialog>

            {/* ════════ Dialog de eliminar ════════ */}
            <Dialog
                visible={eliminarDialogVisible}
                onHide={cerrarDialogEliminar}
                header="Eliminar cotización"
                style={{width: '450px'}}
                modal
                closable={!eliminando}
                draggable={false}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button
                            label="Cancelar"
                            icon="pi pi-times"
                            className="p-button-text p-button-secondary"
                            onClick={cerrarDialogEliminar}
                            disabled={eliminando}
                        />
                        <Button
                            label="Sí, eliminar"
                            icon="pi pi-trash"
                            className="p-button-sm p-button-danger"
                            onClick={confirmarEliminacion}
                            loading={eliminando}
                            disabled={!motivoEliminacion.trim()}
                        />
                    </div>
                }
            >
                {cotizacionAEliminar && (
                    <div className="flex flex-column gap-3">
                        <div className="flex align-items-center gap-2 text-700">
                            <i className="pi pi-exclamation-triangle text-orange-500"/>
                            <span>¿Está seguro de eliminar <strong>"{cotizacionAEliminar.nombre}"</strong>?</span>
                        </div>

                        <div className="flex flex-column gap-2">
                            <label htmlFor="motivo-eliminacion" className="font-medium">
                                Motivo <span className="text-red-500">*</span>
                            </label>
                            <InputTextarea
                                id="motivo-eliminacion"
                                value={motivoEliminacion}
                                onChange={(e) => setMotivoEliminacion(e.target.value)}
                                rows={4}
                                autoResize
                                placeholder="Describe el motivo de la eliminación..."
                                className="w-full"
                                style={{fontSize: '0.85rem'}}
                            />
                            <small className="text-400">{motivoEliminacion.length} caracteres</small>
                        </div>
                    </div>
                )}
            </Dialog>
        </div>
    );
};

export default ListadoCotizaciones;
